import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useContext, useEffect, useState} from "react";
import {
    faCaretDown,
    faCaretRight,
    faCirclePlus,
    faFolderPlus,
    faPencil,
    faTrash
} from "@fortawesome/free-solid-svg-icons";

import "../styles/TemplateDivisionTypeElement.css";
import TemplateDivisionElement from "./TemplateDivisionElement";
import Button from "./Button";
import Modal from "./Modal";
import {ColumnGrid, GridRow} from "./ColumnGrid";
import TextInput from "./TextInput";
import {ContextMenu, ContextMenuItem} from "./ContextMenu";
import {findDivisionType, TemplateContext} from "../pages/MapTemplateEditor";
import _ from 'lodash';

const InlineIconButton = ({ icon, onClick, style }) => {
    return <div style={style} className="InlineIconButton" onClick={onClick}><FontAwesomeIcon icon={icon}/></div>;
}

const DivisionTypeCreateModal = ({ showModal, setShowModal, divisionTypeId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();
    const [ pluralName, setPluralName ] = useState();

    const create = async () => {
        setShowModal(false);

        const modifiedTemplate = _.cloneDeep(template);

        const { result: divisionType } = findDivisionType(divisionTypeId, modifiedTemplate);
        divisionType.division_types.push({ name, pluralName, topLevel: false });

        setTemplate(modifiedTemplate);
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={create} content="Create"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="New Division Type"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
                <GridRow>
                    <span>Plural Name</span>
                    <TextInput onChange={e => setPluralName(e.target.value)}/>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const RenameModal = ({ showModal, setShowModal, divisionTypeId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();
    const [ pluralName, setPluralName ] = useState();

    const rename = async () => {
        setShowModal(false);

        const modifiedTemplate = _.cloneDeep(template);

        const { result: divisionType } = findDivisionType(divisionTypeId, modifiedTemplate);
        divisionType.name = name;
        divisionType.pluralName = pluralName;

        setTemplate(modifiedTemplate);
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={rename} content="Rename"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="Rename"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
                <GridRow>
                    <span>Plural Name</span>
                    <TextInput onChange={e => setPluralName(e.target.value)}/>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const DivisionCreateModal = ({ showModal, setShowModal, divisionTypeId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();

    const create = async () => {
        setShowModal(false);

        const modifiedTemplate = _.cloneDeep(template);

        const { result: divisionType } = findDivisionType(divisionTypeId, modifiedTemplate);
        divisionType.divisions.push({ name, type: divisionTypeId });

        setTemplate(modifiedTemplate);
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={create} content="Create"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="New Division"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const LocalContextMenuItem = ({icon, name, keystroke, onClick }) => {
    return (
        <ContextMenuItem onClick={onClick}>
            <ColumnGrid columns={"30px 1fr 1fr"}>
                <GridRow>
                    <FontAwesomeIcon icon={icon} style={{ margin: "auto" }}/>
                    <span>{name}</span>
                    <span style={{ textAlign: "right", fontWeight: 800 }}>{keystroke}</span>
                </GridRow>
            </ColumnGrid>
        </ContextMenuItem>
    );
}

const TemplateDivisionTypeElement = ({ id, parentOpen = true }) => {
    const [ open, setOpen ] = useState(false);

    const [ showTypeModal, setShowTypeModal ] = useState(false);
    const [ showDivisionModal, setShowDivisionModal ] = useState(false);
    const [ showRenameModal, setShowRenameModal ] = useState(false);

    const [ showContextMenu, setShowContextMenu ] = useState(false);

    const { template } = useContext(TemplateContext);
    const [ data, setData ] = useState(null);

    useEffect(() => setData(findDivisionType(id, template)), [id, template]);

    if (parentOpen === false && open) setOpen(false);

    if (!data) return;

    return (
        <>
            <div className="TemplateDivisionTypeElement">
                <DivisionTypeCreateModal showModal={showTypeModal} setShowModal={setShowTypeModal} divisionTypeId={id}/>
                <DivisionCreateModal showModal={showDivisionModal} setShowModal={setShowDivisionModal} divisionTypeId={id}/>
                <RenameModal showModal={showRenameModal} setShowModal={setShowRenameModal} divisionTypeId={id}/>

                <ContextMenu show={showContextMenu} setShow={setShowContextMenu}>
                    <LocalContextMenuItem icon={faFolderPlus} name="New Subtype" keystroke={"⇧N"} onClick={() => {setShowContextMenu(false); setShowTypeModal(true)}}/>
                    {data.result.topLevel ? <LocalContextMenuItem icon={faCirclePlus} name="New Subdivision" keystroke={"N"} onClick={() => {setShowContextMenu(false); setShowDivisionModal(true)}}/> : <></>}
                    <LocalContextMenuItem icon={faPencil} name="Rename" keystroke={"R"} onClick={() => {setShowContextMenu(false); setShowRenameModal(true)}}/>
                    <LocalContextMenuItem icon={faTrash} name="Delete" keystroke={"⌫"}/>
                </ContextMenu>

                <div className="self" tabIndex={0} style={{ paddingLeft: ((1 + data.depth) * 20) + 'px' }}
                     onContextMenu={e => {setShowContextMenu({ x: e.nativeEvent.clientX, y: e.nativeEvent.clientY }); e.preventDefault();}}
                     onKeyUp={e => {
                         e.stopPropagation();
                         e.preventDefault();
                         setShowContextMenu(false);

                         if (e.key === 'N') setShowTypeModal(true);
                         if (e.key === 'n') setShowDivisionModal(true);
                         if (e.key === 'r') setShowRenameModal(true);
                     }}>
                    <InlineIconButton icon={ open ? faCaretDown : faCaretRight } onClick={() => setOpen(!open)}
                                      style={{ opacity: data.result.division_types?.length || data.result.divisions?.length ? '100%' : 0 }}/>
                    <p className="name">{data.result.divisions?.length ? data.result.pluralName : data.result.name}</p>
                </div>
                <div className="children" style={{ display: open ? "block" : "none" }}>
                    {data.result.division_types?.map(divisionType => <TemplateDivisionTypeElement key={divisionType.id} parentOpen={open} id={divisionType.id}/>)}
                    {data.result.divisions?.map(division => <TemplateDivisionElement key={division.id} id={division.id} parentOpen={open}/>)}
                </div>
            </div>
        </>
    );
}

export default TemplateDivisionTypeElement;
